import React, { useEffect } from "react";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { getUsersByID, getAllRanking } from "../../store/slices/user";
import useAuth from "../../../src/hooks/useAuth";
import styles from "./MyAccountTab.module.css";

// Import bank icons
import scbIcon from "../../assets/bankicon/014.webp";
import truemoneyIcon from "../../assets/bankicon/true.webp";
import ktbIcon from "../../assets/bankicon/006.webp";
import kbankIcon from "../../assets/bankicon/004.webp";
import bblIcon from "../../assets/bankicon/002.webp";
import ttbIcon from "../../assets/bankicon/011.webp";
import gsbIcon from "../../assets/bankicon/030.webp";
import krungsriIcon from "../../assets/bankicon/025.webp";
import baacIcon from "../../assets/bankicon/034.webp";
import uobIcon from "../../assets/bankicon/024.webp";
import ghbIcon from "../../assets/bankicon/033.webp";
import cimbIcon from "../../assets/bankicon/022.webp";
import islamIcon from "../../assets/bankicon/066.webp";
import kkpIcon from "../../assets/bankicon/069.webp";
import promptpayIcon from "../../assets/bankicon/promptpay.webp";

export default function MyAccountTab() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { singleUsers, playerBalance, wheelPoint, allRanking } = useSelector(
    (s) => s.user,
  );
  const account = Array.isArray(singleUsers) ? singleUsers[0] : null;

  // Determine user id (from current user slice, localStorage, or account)
  const storedId =
    user?.id || // prefer authenticated user id
    account?.id ||
    (typeof window !== "undefined" ? localStorage.getItem("userId") : null);

  useEffect(() => {
    dispatch(getAllRanking());
  }, [dispatch]);

  useEffect(() => {
    if (storedId) dispatch(getUsersByID({ id: storedId }));
  }, [storedId, dispatch]);

  // Helper function to get bank logo based on bank code
  const getBankLogo = (bankCode) => {
    switch (bankCode) {
      case "true":
        return truemoneyIcon;
      case "014":
        return scbIcon;
      case "006":
        return ktbIcon;
      case "004":
        return kbankIcon;
      case "002":
        return bblIcon;
      case "011":
        return ttbIcon;
      case "030":
        return gsbIcon;
      case "025":
        return krungsriIcon;
      case "034":
        return baacIcon;
      case "024":
        return uobIcon;
      case "033":
        return ghbIcon;
      case "022":
        return cimbIcon;
      case "066":
        return islamIcon;
      case "069":
        return kkpIcon;
      case "promptpay":
        return promptpayIcon;
      default:
        return scbIcon; // fallback
    }
  };

  // Helper function to get bank name based on bank code
  const getBankName = (bankCode) => {
    switch (bankCode) {
      case "true":
        return "ทรูมันนี่วอลเล็ท";
      case "014":
        return "ธนาคารไทยพาณิชย์";
      case "006":
        return "ธนาคารกรุงไทย";
      case "004":
        return "ธนาคารกสิกรไทย";
      case "002":
        return "ธนาคารกรุงเทพ";
      case "011":
        return "ธนาคารทีทีบี";
      case "030":
        return "ธนาคารออมสิน";
      case "025":
        return "ธนาคารกรุงศรี";
      case "034":
        return "ธนาคารธ.ก.ส.";
      case "024":
        return "ธนาคารUOB";
      case "033":
        return "ธนาคารอาคารสงเคราะห์";
      case "022":
        return "ธนาคารCIMB";
      case "066":
        return "ธนาคารอิสลาม";
      case "069":
        return "ธนาคารเกียรตินาคิน";
      case "promptpay":
        return "พร้อมเพย์";
      default:
        return "ธนาคาร";
    }
  };

  // Map fields
  const apiBase =
    process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "";
  const username = account?.book_name || "-";
  const phone = account?.username || "-"; // username holds phone number per API
  const tempWallet = (account?.cashWallet ?? 0).toLocaleString() + " THB";
  const balanceValue =
    account?.creditWallet !== undefined
      ? account.creditWallet
      : Array.isArray(playerBalance)
        ? playerBalance[0]
        : 0;
  const balance = (balanceValue ?? 0).toLocaleString() + " THB";
  const gems = account?.wheelPoint ?? wheelPoint ?? 0;
  const bankName = account?.bank_name || "";
  const bankAccount = account?.book_number || "";
  const bankHolder = account?.book_name || "";

  // Determine rank index/class (prefer numeric rankClass from account)
  const currentClass = Number(
    account?.rankClass ?? account?.rankclass ?? account?.rank_class ?? 0,
  );

  // Resolve rank objects from allRanking (preferred) or fallback to account fields
  const currentRank =
    (allRanking && allRanking.length > 0 ? allRanking[currentClass] : null) ||
    (account?.rankImageURL || account?.rankName
      ? {
          rankImageURL: account?.rankImageURL,
          rankName: account?.rankName || account?.rankStatus,
          rankReceiveConditionAmount: account?.rankReceiveConditionAmount,
        }
      : null);

  const rankLabel =
    currentRank?.rankName || account?.rankName || account?.rankStatus || "-";

  return (
    <div className={styles.wrapper}>
      {/* ── Hero Banner ── */}
      <div className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.heroAvatar}>
          {currentRank?.rankImageURL ? (
            <img
              className={styles.heroRankImg}
              src={`${apiBase}/uploads${currentRank.rankImageURL}`}
              alt={currentRank?.rankName || "rank"}
              loading="lazy"
            />
          ) : (
            <Icon icon="solar:user-rounded-bold-duotone" width={56} height={56} />
          )}
          <div className={styles.heroRankTag}>
            {currentRank?.rankName || rankLabel || "สมาชิก"}
          </div>
        </div>
        <h2 className={styles.heroName}>{username}</h2>
        <p className={styles.heroPhone}>
          <Icon icon="solar:phone-bold" width={14} />
          {phone}
        </p>
      </div>

      {/* ── Wallet Strip ── */}
      <div className={styles.walletStrip}>
        <div className={styles.walletItem}>
          <Icon icon="solar:wallet-money-bold-duotone" width={22} className={styles.walletIcon} />
          <div className={styles.walletMeta}>
            <span className={styles.walletLabel}>กระเป๋าเงิน</span>
            <span className={styles.walletAmount}>{tempWallet}</span>
          </div>
        </div>
        <div className={`${styles.walletItem} ${styles.walletHighlight}`}>
          <Icon icon="solar:dollar-minimalistic-bold-duotone" width={22} className={styles.walletIcon} />
          <div className={styles.walletMeta}>
            <span className={styles.walletLabel}>เครดิตคงเหลือ</span>
            <span className={styles.walletAmount}>{balance}</span>
          </div>
        </div>
        <div className={styles.walletItem}>
          <Icon icon="ri:copper-diamond-fill" width={20} className={styles.walletIcon} />
          <div className={styles.walletMeta}>
            <span className={styles.walletLabel}>เพชรสะสม</span>
            <span className={styles.walletAmount}>{gems}</span>
          </div>
        </div>
      </div>

      <div className={styles.columns}>
      {/* ── ข้อมูลสมาชิก ── */}
      <div className={styles.column}>
      <div className={styles.sectionTitle}>
        <Icon icon="solar:user-id-bold-duotone" width={20} />
        ข้อมูลสมาชิก
      </div>
      <div className={styles.infoGrid}>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>ชื่อ-นามสกุล</span>
          <span className={styles.infoValue}>{username}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>เบอร์โทรศัพท์</span>
          <span className={styles.infoValue}>{phone}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>ระดับสมาชิก</span>
          <span className={`${styles.infoValue} ${styles.infoRank}`}>
            {currentRank?.rankName || rankLabel || "-"}
          </span>
        </div>
      </div>

      </div>

      {/* ── บัญชีธนาคาร ── */}
      <div className={styles.column}>
      <div className={styles.sectionTitle}>
        <Icon icon="solar:banknote-2-bold-duotone" width={20} />
        บัญชีธนาคาร
      </div>

      {bankAccount ? (
        <div className={styles.bankCard}>
          <div className={styles.bankLeft}>
            <div className={styles.bankLogoBox}>
              <img
                src={getBankLogo(bankName)}
                alt={getBankName(bankName)}
                className={styles.bankLogo}
              />
            </div>
            <div className={styles.bankMeta}>
              <span className={styles.bankNameText}>
                {getBankName(bankName)}
              </span>
              <span className={styles.bankAccNum}>{bankAccount}</span>
              {bankHolder && (
                <span className={styles.bankHolder}>{bankHolder}</span>
              )}
            </div>
          </div>
          <div className={styles.bankVerified}>
            <Icon icon="solar:verified-check-bold" width={18} />
            ผูกบัญชีแล้ว
          </div>
        </div>
      ) : (
        <div className={styles.bankEmpty}>
          <Icon icon="solar:banknote-2-bold-duotone" width={30} className={styles.bankEmptyIcon} />
          <span className={styles.bankEmptyText}>ยังไม่ได้ผูกบัญชีธนาคาร</span>
          <span className={styles.bankEmptyHint}>
            กรุณาติดต่อฝ่ายบริการเพื่อเพิ่มข้อมูลบัญชี
          </span>
        </div>
      )}
      </div>
      </div>
    </div>
  );
}
